(function initLunastraDiyData(root) {
  function deepFreeze(value) {
    if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
    Reflect.ownKeys(value).forEach((key) => deepFreeze(value[key]));
    return Object.freeze(value);
  }

  const SIZE_LABELS = deepFreeze({
    6: 'Delicate',
    8: 'Classic',
    10: 'Signature',
    12: 'Focal'
  });

  const FIT_ALLOWANCE_CM = deepFreeze({
    snug: 0.5,
    balanced: 1,
    relaxed: 1.5
  });

  const ROLE_SIZE_ORDER = deepFreeze({
    focal: [12, 10, 8, 6],
    main: [10, 8, 12, 6],
    accent: [10, 8, 6, 12],
    support: [8, 10, 6, 12],
    transition: [6, 8, 10, 12],
    manual: [10, 8, 6, 12]
  });

  const NAME_ALIASES = deepFreeze({
    'black obsidian': 'obsidian',
    obsidian: 'obsidian',
    'milky quartz': 'clear-quartz'
  });

  const crystals = deepFreeze([
    {
      id: 'clear-quartz', name: 'Clear Quartz', assetKey: 'clearQuartz', icon: 'CQ',
      intentions: ['Clarity', 'Focus'], color: '#e8e8e8', dark: '#a0a0a0',
      glow: 'rgba(255,255,255,0.5)', visualScale: 2.04, refractiveBand: true,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard', 12: 'focal' },
      legacyPrices: { 6: 0.8, 8: 1.2, 10: 1.8 }
    },
    {
      id: 'rose-quartz', name: 'Rose Quartz', assetKey: 'roseQuartz', icon: 'RQ',
      intentions: ['Love', 'Compassion'], color: '#e8a9c9', dark: '#b06a8a',
      glow: 'rgba(232,169,201,0.6)', visualScale: 1.35, refractiveBand: true,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard', 12: 'focal' },
      legacyPrices: { 6: 0.9, 8: 1.2, 10: 1.8 }
    },
    {
      id: 'amethyst', name: 'Amethyst', assetKey: 'amethyst', icon: 'AM',
      intentions: ['Sleep', 'Protection', 'Intuition'], color: '#9b7bb8', dark: '#5a3d6e',
      glow: 'rgba(155,123,184,0.6)', visualScale: 1.36, refractiveBand: true,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard', 12: 'focal' },
      legacyPrices: { 6: 0.9, 8: 1.2, 10: 1.8 }
    },
    {
      id: 'moonstone', name: 'Moonstone', assetKey: 'moonstone', icon: 'MO',
      intentions: ['Intuition', 'Balance', 'New Beginnings'], color: '#c5d9e8', dark: '#879caf',
      glow: 'rgba(186,220,255,0.48)', visualScale: 1.24, refractiveBand: true,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard', 12: 'focal' },
      legacyPrices: { 6: 0.9 }
    },
    {
      id: 'citrine', name: 'Citrine', assetKey: 'citrine', icon: 'CT',
      intentions: ['Career', 'Confidence', 'Abundance'], color: '#d4a574', dark: '#8a6a3a',
      glow: 'rgba(212,165,116,0.6)', visualScale: 1.9,
      sizes: { 8: 'standard', 10: 'standard', 12: 'focal' },
      legacyPrices: { 8: 1.2, 10: 1.8 }
    },
    {
      id: 'tiger-eye', name: 'Tiger Eye', assetKey: 'tigerEye', icon: 'TE',
      intentions: ['Confidence', 'Career', 'Grounding'], color: '#9b641f', dark: '#4b2b12',
      glow: 'rgba(208,146,62,0.52)', visualScale: 1.79,
      sizes: { 8: 'standard', 10: 'standard', 12: 'focal' }, legacyPrices: {}
    },
    {
      id: 'obsidian', name: 'Obsidian', displayName: 'Black Obsidian', assetKey: 'obsidian', icon: 'OB',
      intentions: ['Protection', 'Grounding', 'Strength'], color: '#19191c', dark: '#030305',
      glow: 'rgba(255,255,255,0.18)', visualScale: 2.02,
      sizes: { 8: 'standard', 10: 'standard', 12: 'focal' },
      legacyPrices: { 8: 1.2 }
    },
    {
      id: 'labradorite', name: 'Labradorite', assetKey: 'labradorite', icon: 'LB',
      intentions: ['Transformation', 'Intuition', 'Protection'], color: '#547b87', dark: '#273b45',
      glow: 'rgba(93,174,196,0.48)', visualScale: 2,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard', 12: 'focal' }, legacyPrices: {}
    },
    {
      id: 'lepidolite', name: 'Lepidolite', assetKey: 'lepidolite', icon: 'LP',
      intentions: ['Calm', 'Sleep', 'Balance'], color: '#a88bb5', dark: '#624c72',
      glow: 'rgba(174,141,191,0.48)', visualScale: 1.6,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard' }, legacyPrices: {}
    },
    {
      id: 'green-aventurine', name: 'Green Aventurine', assetKey: 'greenAventurine', icon: 'GA',
      intentions: ['Luck', 'Opportunity', 'Growth'], color: '#6a9b7d', dark: '#355c48',
      glow: 'rgba(119,177,141,0.46)', visualScale: 1.76,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard' }, legacyPrices: {}
    },
    {
      id: 'amazonite', name: 'Amazonite', assetKey: 'amazonite', icon: 'AZ',
      intentions: ['Harmony', 'Communication', 'Balance'], color: '#6ca6a4', dark: '#315f63',
      glow: 'rgba(112,187,184,0.46)', visualScale: 2.11,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard' }, legacyPrices: {}
    },
    {
      id: 'sunstone', name: 'Sunstone', assetKey: 'sunstone', icon: 'SU',
      intentions: ['Vitality', 'Joy', 'Confidence'], color: '#c77f5b', dark: '#74442f',
      glow: 'rgba(225,144,101,0.5)', visualScale: 1.65,
      sizes: { 6: 'standard', 8: 'standard', 10: 'standard' }, legacyPrices: {}
    },
    {
      id: 'black-tourmaline', name: 'Black Tourmaline', assetKey: 'blackTourmaline', icon: 'BT',
      intentions: ['Protection', 'Grounding', 'Boundaries'], color: '#121418', dark: '#020305',
      glow: 'rgba(255,255,255,0.14)', visualScale: 1.88,
      sizes: { 8: 'standard', 10: 'standard' }, legacyPrices: {}
    },
    {
      id: 'smoky-quartz', name: 'Smoky Quartz', assetKey: 'smokyQuartz', icon: 'SQ',
      intentions: ['Stability', 'Grounding', 'Calm'], color: '#6e594d', dark: '#332720',
      glow: 'rgba(154,126,104,0.42)', visualScale: 2.12,
      sizes: { 8: 'standard', 10: 'standard' }, legacyPrices: {}
    },
    {
      id: 'pyrite', name: 'Pyrite', assetKey: 'pyrite', icon: 'PY',
      intentions: ['Abundance', 'Confidence', 'Vitality'], color: '#b49b68', dark: '#5f5646',
      glow: 'rgba(212,184,118,0.46)', visualScale: 1.92,
      sizes: { 8: 'standard', 10: 'accent' },
      legacyPrices: { 8: 1.3, 10: 2 }
    }
  ].map((crystal) => ({ ...crystal, defaultSize: 10 })));

  function slug(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function resolveCrystalId(idOrName) {
    const normalized = String(idOrName || '').trim().toLowerCase();
    if (NAME_ALIASES[normalized]) return NAME_ALIASES[normalized];
    const direct = crystals.find((crystal) => crystal.id === normalized);
    if (direct) return direct.id;
    const byName = crystals.find((crystal) =>
      crystal.name.toLowerCase() === normalized || String(crystal.displayName || '').toLowerCase() === normalized
    );
    return byName ? byName.id : slug(normalized);
  }

  function crystalFor(idOrName) {
    const id = resolveCrystalId(idOrName);
    return crystals.find((crystal) => crystal.id === id) || null;
  }

  function availableSizes(idOrName) {
    const crystal = crystalFor(idOrName);
    return crystal ? Object.keys(crystal.sizes).map(Number).sort((a, b) => a - b) : [];
  }

  function variantFor(idOrName, size, assets) {
    const crystal = crystalFor(idOrName);
    const numericSize = Number(size);
    if (!crystal || !availableSizes(crystal.id).includes(numericSize)) return null;
    return {
      id: `${crystal.id}-${numericSize}`,
      crystalId: crystal.id,
      name: crystal.displayName || crystal.name,
      canonicalName: crystal.name,
      size: numericSize,
      sizeLabel: SIZE_LABELS[numericSize],
      availabilityRole: crystal.sizes[numericSize],
      price: Number(crystal.legacyPrices[numericSize] || 0),
      icon: crystal.icon,
      image: (assets || {})[crystal.assetKey] || '',
      color: crystal.color,
      dark: crystal.dark,
      glow: crystal.glow,
      category: crystal.id,
      intentions: [...crystal.intentions],
      visualScale: crystal.visualScale,
      refractiveBand: Boolean(crystal.refractiveBand)
    };
  }

  function createCatalog(assets) {
    return crystals.flatMap((crystal) =>
      availableSizes(crystal.id).map((size) => variantFor(crystal.id, size, assets))
    );
  }

  function hydrateSavedBead(bead, index, assets, uidFactory) {
    const saved = bead && typeof bead === 'object' ? bead : {};
    const crystal = [saved.crystalId, saved.canonicalName, saved.name]
      .filter((identifier) => String(identifier || '').trim())
      .map((identifier) => crystalFor(identifier))
      .find(Boolean);
    if (!crystal) return null;
    const crystalId = crystal.id;

    const requestedSize = Number(saved.size);
    const sizes = availableSizes(crystalId);
    const canonicalSize = chooseManualSize({ crystalId, selectedSize: requestedSize });
    const variant = variantFor(crystalId, canonicalSize, assets);
    const preserveLegacySize = Number.isFinite(requestedSize)
      && requestedSize > 0
      && !sizes.includes(requestedSize);
    const effectiveSize = preserveLegacySize ? requestedSize : canonicalSize;
    const rawPrice = saved.price;
    const hasSavedPrice = rawPrice !== null
      && rawPrice !== undefined
      && !(typeof rawPrice === 'string' && rawPrice.trim() === '');
    const savedPrice = hasSavedPrice ? Number(rawPrice) : NaN;
    const validSavedPrice = Number.isFinite(savedPrice) && savedPrice >= 0;
    const generatedUid = typeof uidFactory === 'function' ? uidFactory(index) : '';
    const uid = String(saved.uid || generatedUid || `restored-${index}`).trim() || `restored-${index}`;

    return {
      ...variant,
      id: preserveLegacySize ? `${crystalId}-${effectiveSize}-legacy` : variant.id,
      size: effectiveSize,
      sizeLabel: preserveLegacySize ? 'Legacy size' : SIZE_LABELS[canonicalSize],
      availabilityRole: preserveLegacySize ? 'legacy' : variant.availabilityRole,
      legacyUnavailableSize: preserveLegacySize,
      price: validSavedPrice ? savedPrice : (preserveLegacySize ? 0 : Number(variant.price || 0)),
      role: saved.role || 'manual',
      sequence: Number.isFinite(Number(saved.sequence)) ? Number(saved.sequence) : index,
      uid,
      fitAdjustment: Boolean(saved.fitAdjustment)
    };
  }

  function chooseManualSize({ crystalId, selectedSize }) {
    const crystal = crystalFor(crystalId);
    const sizes = availableSizes(crystalId);
    const requested = Number(selectedSize);
    if (sizes.includes(requested)) return requested;
    const defaultSize = crystal ? Number(crystal.defaultSize) : 10;
    return sizes.includes(defaultSize) ? defaultSize : sizes[0];
  }

  function chooseGeneratedSize({ crystalId, explicitSize, role }) {
    const sizes = availableSizes(crystalId);
    const explicit = Number(explicitSize);
    if (sizes.includes(explicit)) return explicit;
    const order = ROLE_SIZE_ORDER[role] || ROLE_SIZE_ORDER.main;
    return order.find((size) => sizes.includes(size)) || chooseManualSize({ crystalId });
  }

  function capGeneratedFocals(beads, maximum, assets) {
    const focalLimit = Number.isFinite(Number(maximum)) ? Number(maximum) : 3;
    let focalCount = 0;
    return beads.map((bead) => {
      if (bead.size !== 12 || bead.role !== 'focal') return bead;
      focalCount += 1;
      if (focalCount <= focalLimit) return bead;
      const replacement = variantFor(bead.crystalId, 10, assets) || variantFor(bead.crystalId, 8, assets);
      return { ...replacement, uid: bead.uid, role: bead.role };
    });
  }

  function calculateInnerCircumferenceCm(beads) {
    const diameters = (beads || []).map((bead) => Number(bead.size)).filter((size) => size > 0);
    if (!diameters.length) return 0;
    const totalDiameterMm = diameters.reduce((sum, size) => sum + size, 0);
    const averageDiameterMm = totalDiameterMm / diameters.length;
    const innerCircumferenceMm = Math.max(0, totalDiameterMm - Math.PI * averageDiameterMm);
    return Math.round(innerCircumferenceMm) / 10;
  }

  function targetInnerCircumferenceCm(wristCm, fit) {
    const normalizedFit = fit === 'tight' ? 'snug' : fit;
    const allowance = FIT_ALLOWANCE_CM[normalizedFit] ?? FIT_ALLOWANCE_CM.balanced;
    return Math.round((Number(wristCm || 0) + allowance) * 10) / 10;
  }

  function maximumBeadCapacity({ wristCm = 30, fit = 'relaxed', minimumBeadSize = 6 } = {}) {
    const beadSize = Number(minimumBeadSize);
    const targetMm = targetInnerCircumferenceCm(wristCm, fit) * 10;
    return Math.ceil((targetMm + Math.PI * beadSize) / beadSize);
  }

  function toInches(centimeters) {
    return Math.round((Number(centimeters || 0) / 2.54) * 100) / 100;
  }

  const api = Object.freeze({
    source: 'lunastra-mother-crystals-v2',
    recommendationBasis: ['Birth Date', 'Personal Intention', 'Balancing Crystal'],
    sizeLabels: SIZE_LABELS,
    fitAllowances: FIT_ALLOWANCE_CM,
    crystals,
    resolveCrystalId,
    crystalFor,
    availableSizes,
    variantFor,
    createCatalog,
    hydrateSavedBead,
    chooseManualSize,
    chooseGeneratedSize,
    capGeneratedFocals,
    calculateInnerCircumferenceCm,
    targetInnerCircumferenceCm,
    maximumBeadCapacity,
    toInches
  });

  root.LunastraDiyData = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
}(typeof window !== 'undefined' ? window : globalThis));
